<template>
  <div class="row" v-if="!vismaBoardingId">
    <div class="col-md-6">
      <div class="form-group">
        <label for="visma_package">{{ _('Visma price package') }}</label>
        <v-select id="visma_package" :options="vismaPackages" v-model="vismaPackage" label="name" :keys="['name']" :placeholder="_('Visma price package')">
          <template v-slot:no-options="{ searching }">
            <template v-if="searching">
              {{ _('No results found.') }}
            </template>
            <template v-else>{{ _('Visma price package') }}</template>
          </template>
        </v-select>
      </div>
    </div>
    <div class="col-md-6">
      <div class="form-group">
        <label for="visma_boarding">{{ _('Company ID') }}</label>
        <div :class="isCompanyIdValid ? 'input-group has-success' : 'input-group has-error'">
          <input type="text" class="form-control" v-model="companyId" placeholder="1234567-8" />
          <span class="input-group-btn">
            <button type="button" class="btn btn-info" v-on:click="beginVismaBoarding()" :disabled="submitting">{{ _('Create application') }}</button>
          </span>
        </div>
      </div>
    </div>
  </div>
  <div class="row" v-else>
    <div class="col">
      <p>
        {{ _('Visma Pay Express web form and more information has been sent to your email. You can also access the web form with this link:') }}
        <a :href="'https://signup.vismapay.com/' + vismaBoardingId" target="_blank">https://signup.vismapay.com/{{ vismaBoardingId }}</a></p>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import components from '~/lib/components'
import { FinnishBusinessIds } from 'finnish-business-ids'

export default {
  data () {
    return {
      vismaPackages: [
        {
          name: 'Express',
          code: 'express-johku-skip-onboard'
        },
        {
          name: 'Power',
          code: 'power-johku-skip-onboard'
        },
        {
          name: 'Power+',
          code: 'power-plus-johku-skip-onboard'
        }
      ],
      vismaPackage: {},
      vismaBoardingId: '',
      companyId: '',
      submitting: false
    }
  },
  mixins: [components],
  computed: {
    ...mapGetters(['localDomain']),
    isCompanyIdValid () {
      if (FinnishBusinessIds.isValidBusinessId(this.companyId)) {
        return true
      } else {
        return false
      }
    }
  },
  beforeMount () {
    this.vismaPackage = this.vismaPackages[0]
    this.companyId = this.settings.companyid ? this.settings.companyid : ''
  },
  methods: {
    isStreetAddressField (key) {
      return key === 'companyAddressStreet' || key === 'companyBillingAddressStreet'
    },
    sanitizeStreetAddress (value) {
      return value ? String(value).replace(/\r\n|\r|\n/g, ' ').trim() : ''
    },
    /**
     * beginBoardingModel
     * 
     * Either takes formData as argument, which puts data into the boardingModel that is sent to vismaypay boarding process
     * Or if formData is not given (it's empty) try to look for settings in this.settings
     */
    async beginVismaBoarding (boardingData = {}) {
      // Make sure we have the required fields
      if (!this.vismaPackage || !this.vismaPackage.code) {
        return this.showError(this._('Price package must be selected'))
      }
      if (!this.companyId) {
        return this.showError(this._('Company id is required'))
      }
      if (!this.isCompanyIdValid) {
        return this.showError(this._('Invalid company id number'))
      }

      this.submitting = true

      // Create our boarding model
      const vatNumber = 'FI' + this.companyId.replace('-', '')
      let boardingModel = {
        pricePackage: this.vismaPackage.code,
        formData: {},
        lang: 'fi',
        vatNumber: vatNumber,
        partnerCode: '27353842'
      }

      // Populate our boarding model from boardingData or from settings
      if (Object.keys(boardingData).length) {
        for (const [key, value] of Object.entries(boardingData)) {
          boardingModel.formData[key] = this.isStreetAddressField(key) ? this.sanitizeStreetAddress(value) : value
        }
      } else {
        if (this.settings) {
          let model = boardingModel.formData

          model.companyName = this.settings.companyname ? this.settings.companyname.trim() : ''
          model.companyAddressStreet = this.sanitizeStreetAddress(this.settings.companyaddress)
          model.channelName = 'Johku'
          model.channelId = 1099 // Hard-coded Johku channel id
          model.companyPostal = this.settings.companyzip ? this.settings.companyzip.trim() : ''
          model.companyCity = this.settings.companycity ? this.settings.companycity.trim() : ''
          model.companyPhone = this.settings.companyphone ? this.settings.companyphone.trim() : ''
          model.companyBillingEmail = this.settings.billingemail ? this.settings.billingemail.trim() : this.settings.companyemail.trim()
          model.companyBillingAddressStreet = this.sanitizeStreetAddress(this.settings.companyaddress)
          model.companyBillingPostal = this.settings.companyzip ? this.settings.companyzip.trim() : ''
          model.companyBillingCity = this.settings.companycity ? this.settings.companycity.trim() : ''
          model.marketingName = this.settings.marketingname ? this.settings.marketingname.trim() : ''
          model.bankIban = this.settings.pay_option1_iban ? this.settings.pay_option1_iban.replace(/[^A-Za-z0-9]/g, '').trim() : ''
          model.bankName = this.settings.pay_option1_bank ? this.settings.pay_option1_bank.trim() : ''
          model.websiteUrl = this.localDomain ? this.localDomain : ''
        } else {
          // We did not have formData and we did not have settings, nothing to do
          return false
        }
      }

      // Save company id to settings
      try {
        await axios.put(this.baseDomain + this.shopId + '/settings/1.json?apikey=' + this.apiKey, {
          companyid: this.companyId,
          companyprofile: 1,
          version: this.version
        })
      } catch (error) {
        this.showError(this._('Could not save company id'))
      }

      try {
        const { data: response } = await axios.post(this.localDomain + 'vismaboarding', boardingModel)
        if (response) {
          const { data } = await axios.post(this.baseDomain + this.shopId + '/vismapayboarding.json?apikey=' + this.apiKey, { vismapayboardinglink: response.id })
          if (data) {
            this.vismaBoardingId = response.id
          }
        }
        this.submitting = false
      } catch (error) {
        this.showError(error.errorMessage || this._('There was an error creating the application. Please check that you have entered all company and billing information in the Company Profile.'))
        this.submitting = false
      }
    }
  }  
}
</script>